import { EventEmitter } from 'events';
import { WorkspaceFolder } from 'vscode-languageserver-protocol';
import { URI } from 'vscode-uri';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { fsPathToUri, parseURIString } from './utils';
import { DirectoryWalker, FileChangeHandler } from './dir';

export type WorkspaceFileUpdate = {
  fileUri: URI;
  event: 'add' | 'change' | 'unlink';
  workspaceFolder: WorkspaceFolder;
};

export type WorkspaceFilesUpdate = {
  files: URI[];
  workspaceFolder: WorkspaceFolder;
};

export enum VirtualFileSystemEvents {
  WorkspaceFileEvent = 'workspaceFileEvent',
  WorkspaceFilesEvent = 'workspaceFilesEvent',
}
const FILE_SYSTEM_EVENT_NAME = 'fileSystemEvent';

export interface FileSystemEventMap {
  [VirtualFileSystemEvents.WorkspaceFileEvent]: WorkspaceFileUpdate;
  [VirtualFileSystemEvents.WorkspaceFilesEvent]: WorkspaceFilesUpdate;
}

export interface FileSystemEventListener {
  <T extends VirtualFileSystemEvents>(eventType: T, data: FileSystemEventMap[T]): void;
}

export interface VirtualFileSystemService extends DefaultVirtualFileSystemService {}

export const VirtualFileSystemService = createInterfaceId<VirtualFileSystemService>(
  'VirtualFileSystemService',
);

@Injectable(VirtualFileSystemService, [DirectoryWalker])
export class DefaultVirtualFileSystemService {
  #directoryWalker: DirectoryWalker;

  #emitter = new EventEmitter();

  constructor(directoryWalker: DirectoryWalker) {
    this.#directoryWalker = directoryWalker;
  }

  #handleFileChange: FileChangeHandler = (event, workspaceFolder, filePath) => {
    const fileUri = fsPathToUri(filePath);
    this.#emitFileSystemEvent(VirtualFileSystemEvents.WorkspaceFileEvent, {
      event,
      fileUri,
      workspaceFolder,
    });
  };

  /**
   * Emits a workspace files update event for the given workspace folder.
   * This is used to notify consumers of the virtual file system that the files in the workspace have changed.
   */
  async emitFilesForWorkspace(workspaceFolder: WorkspaceFolder): Promise<void> {
    const files = await this.#directoryWalker.findFilesForDirectory({
      directoryUri: parseURIString(workspaceFolder.uri),
    });
    this.#emitFileSystemEvent(VirtualFileSystemEvents.WorkspaceFilesEvent, {
      files,
      workspaceFolder,
    });

    await this.#directoryWalker.disposeWatcherForWorkspace(workspaceFolder);
    this.#directoryWalker.setupFileWatcherForWorkspace(workspaceFolder, this.#handleFileChange);
  }

  #emitFileSystemEvent<T extends VirtualFileSystemEvents>(
    eventType: T,
    data: FileSystemEventMap[T],
  ) {
    this.#emitter.emit(FILE_SYSTEM_EVENT_NAME, eventType, data);
  }

  /**
   * Adds a listener for the file system event.
   * This can be of type `WorkspaceFileUpdate` or `WorkspaceFilesUpdate`.
   *
   * @param listener - The listener to add.
   * @returns A disposable object that can be used to remove the listener.
   */
  onFileSystemEvent(listener: FileSystemEventListener) {
    this.#emitter.on(FILE_SYSTEM_EVENT_NAME, listener);
    return {
      dispose: () => this.#emitter.removeListener(FILE_SYSTEM_EVENT_NAME, listener),
    };
  }
}
